import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  Skeleton,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import { useContext, useMemo, useState, useEffect } from "react";
import { BsChevronDown, BsChevronUp, BsChevronExpand } from "react-icons/bs";
import { FaSearch } from "react-icons/fa";
import { useQuery } from "react-query";
import { useTable, useSortBy, Column } from "react-table";

import PaginationBar from "../../../components/CustomDataGrid/PaginationBar";
import { useDashboard } from "../../../contexts/DashboardContext";
import DashboardService from "../../../services/dashboard.service";
import { ProjectSortField, SortOrder } from "../../../types/dashboard";
import { DashboardSourceInfo } from "./DashboardSourceInfo";

// Keyframe para efeito de shimmer
const shimmer = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`;

// Componente de Skeleton personalizado com efeito de shimmer
const ShimmerSkeleton = styled(Box)(({ theme }) => ({
  display: "inline-block",
  position: "relative",
  backgroundColor: "#f0f0f0",
  backgroundImage:
    "linear-gradient(to right, #f0f0f0 8%, #e0e0e0 18%, #f0f0f0 33%)",
  backgroundSize: "800px 104px",
  animation: `${shimmer} 1.5s linear infinite`,
  borderRadius: 4,
}));

interface IEventRow {
  id: string;
  mmCode: number;
  yearCode: number;
  eventName: string;
  clientName: string;
}

interface PagedResponse {
  total: number;
  data: IEventRow[];
}

export const EventsList = () => {
  const { currentFilters, loading: dashboardLoading, setFilteredProjectsTotal } = useDashboard();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [localSortBy, setLocalSortBy] = useState<string | undefined>();
  const [localSortOrder, setLocalSortOrder] = useState<string | undefined>();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Estabiliza a chave de query com primitivas extraídas dos filtros para
  // evitar refetches desnecessários quando apenas a referência do objeto muda.
  const stableFilterKey = useMemo(() => JSON.stringify({
    cellId: currentFilters.cellId,
    clientId: currentFilters.clientId,
    collaboratorId: currentFilters.collaboratorId,
    startDate: currentFilters.startDate,
    endDate: currentFilters.endDate,
    status: currentFilters.status,
  }), [
    currentFilters.cellId,
    currentFilters.clientId,
    currentFilters.collaboratorId,
    currentFilters.startDate?.start,
    currentFilters.startDate?.end,
    currentFilters.endDate?.start,
    currentFilters.endDate?.end,
    currentFilters.status?.join(","),
  ]);

  // Resetar para página 0 quando filtros ou busca mudam
  useEffect(() => {
    setPage(0);
  }, [currentFilters, debouncedSearchTerm]);

  // Implementar debounce de 3 segundos
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 3000);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const { isLoading, data: rawData } = useQuery<PagedResponse>(
    [
      "dashboard-events",
      stableFilterKey,
      debouncedSearchTerm,
      localSortBy,
      localSortOrder,
      page,
      rowsPerPage,
    ],
    () => {
      return DashboardService.getProjects({
        ...currentFilters,
        searchQuery: debouncedSearchTerm || undefined,
        sortBy: localSortBy as ProjectSortField,
        sortOrder: localSortOrder as SortOrder,
        page,
      });
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  );

  const totalRows = rawData?.total ?? 0;
  const pageItems = rawData?.data ?? [];

  useEffect(() => {
    if (rawData?.total !== undefined) {
      setFilteredProjectsTotal(rawData.total);
    }
  }, [rawData?.total]);

  const columns = useMemo<Column<IEventRow>[]>(
    () => [
      {
        Header: "MM",
        accessor: (row) => row.mmCode,
        id: "mmCode",
        Cell: ({ row }: any) => (
          <span>{`${row.original.mmCode}/${row.original.yearCode}`}</span>
        ),
        width: 120,
        canSort: true,
      },
      {
        Header: "Nome do evento",
        accessor: (row) => row.eventName,
        id: "eventName",
        Cell: ({ row }: any) => <span>{row.original.eventName || "---"}</span>,
        width: 300,
        canSort: true,
      },
      {
        Header: "Cliente",
        accessor: (row) => row.clientName,
        id: "clientName",
        Cell: ({ row }: any) => <span>{row.original.clientName || "---"}</span>,
        width: 300,
        canSort: true,
      },
    ],
    [],
  );

  const tableData = useMemo(() => pageItems, [pageItems]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { sortBy },
  } = useTable(
    {
      columns,
      data: tableData,
      initialState: {
        sortBy: [{ id: "mmCode", desc: true }],
      },
      manualSortBy: true,
      autoResetSortBy: false,
      disableSortRemove: true,
    },
    useSortBy,
  );

  // Efeito para enviar os parâmetros de ordenação para a API
  useEffect(() => {
    if (sortBy && sortBy.length > 0) {
      const column = sortBy[0];
      const apiSortBy = column.id as ProjectSortField;

      // Alterar para sempre começar com ordenação decrescente (desc)
      // Se a coluna clicada for diferente da atual, defina como desc=true
      // Se for a mesma coluna, alterne a direção
      const isNewColumn = localSortBy !== apiSortBy;
      const apiSortOrder = isNewColumn
        ? SortOrder.DESC
        : column.desc
        ? SortOrder.DESC
        : SortOrder.ASC;

      // Usar variáveis de estado locais em vez de modificar diretamente currentFilters
      setLocalSortBy(apiSortBy);
      setLocalSortOrder(apiSortOrder);

      // Não precisa chamar refetch explicitamente, a alteração das dependências da query
      // vai disparar automaticamente a refetch
    }
  }, [sortBy, localSortBy]);

  // Componente de ordenação das colunas
  const ColumnSort = ({ column }: { column: any }) => {
    return (
      <span style={{ margin: "8px 3px 0px 3px" }}>
        {column.isSorted ? (
          column.isSortedDesc ? (
            <BsChevronDown />
          ) : (
            <BsChevronUp />
          )
        ) : (
          <BsChevronExpand />
        )}
      </span>
    );
  };

  return (
    <Card sx={{ px: 2, pb: 2, pt: 3, position: "relative" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <TextField
          placeholder="Pesquisar"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: "300px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaSearch />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
            Valores baseados no Resumo Financeiro
          </Typography>
          <DashboardSourceInfo context="Faturamento e Receita por projeto" />
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "none",
          border: "none",
          maxHeight: "400px",
          minHeight: "300px",
          overflow: "auto",
        }}
      >
        <Table stickyHeader {...getTableProps()}>
          <TableHead>
            {headerGroups.map((headerGroup) => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column: any) => (
                  <TableCell
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    sx={{
                      fontWeight: "600",
                      color: "#4B465C",
                      width: column.width,
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <span>{column.render("Header")}</span>
                      {column.canSort && <ColumnSort column={column} />}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>

          {tableData.length > 0 ? (
            <TableBody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row);
                return (
                  <TableRow {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <TableCell
                        {...cell.getCellProps()}
                        sx={{ fontSize: "12px", py: 1 }}
                      >
                        {cell.render("Cell")}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          ) : isLoading || dashboardLoading ? (
            <TableBody>
              {Array.from(new Array(5)).map((_, index) => (
                <TableRow key={`skeleton-row-${index}`}>
                  <TableCell>
                    <ShimmerSkeleton height={20} width="60%" />
                  </TableCell>
                  <TableCell>
                    <ShimmerSkeleton height={20} width="80%" />
                  </TableCell>
                  <TableCell>
                    <ShimmerSkeleton height={20} width="70%" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          ) : (
            <TableBody>
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 5 }}>
                  {"Nenhum evento encontrado"}
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </TableContainer>

      {totalRows > 0 ? (
        <Box
          sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}` }}
        >
          <PaginationBar
            count={totalRows}
            page={page}
            rowsPerPage={rowsPerPage}
            givenPage={page}
            total={totalRows}
            onPageChange={(newPage) => setPage(newPage)}
            onRowsPerPageChange={(newRowsPerPage) => {
              setRowsPerPage(newRowsPerPage);
              setPage(0);
            }}
            sx={{ border: "none", boxShadow: "none" }}
          />
        </Box>
      ) : null}
    </Card>
  );
};
